import { useEffect, useState, useCallback } from "react";
import { ScrollText, Handshake, UserCheck, AlertTriangle, ShieldAlert, Boxes } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, TenantPicker, useTenantScope, Badge } from "./shared";

function Kpi({ icon: Icon, label, value, tone = "teal", testid }) {
  const tones = {
    teal: "text-teal-600 bg-teal-50 dark:bg-teal-500/10",
    red: "text-red-600 bg-red-50 dark:bg-red-500/10",
    amber: "text-amber-600 bg-amber-50 dark:bg-amber-500/10",
    rose: "text-rose-600 bg-rose-50 dark:bg-rose-500/10",
    sky: "text-sky-600 bg-sky-50 dark:bg-sky-500/10",
  };
  return (
    <div className="bg-card rounded-xl border border-border p-4 flex items-center gap-3" data-testid={testid}>
      <div className={`h-10 w-10 rounded-lg grid place-items-center ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
      <div>
        <div className="text-2xl font-semibold text-foreground leading-none">{value}</div>
        <div className="text-xs text-muted-foreground mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function ComplianceDashboard() {
  const scope = useTenantScope();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/v1/compliance/dashboard", { params: scope.params });
      setData(res.data.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [scope.params]);
  useEffect(() => { load(); }, [load]);

  const d = data || { consents: {}, dsar: { byStatus: {} }, inventory: {}, retention: {} };

  return (
    <div data-testid="compliance-dashboard-page">
      <PageHeader icon={ScrollText} title="Compliance Center"
        subtitle="Consent · Privacy Requests · Retention · Data Inventory">
        <TenantPicker {...scope} />
      </PageHeader>

      {loading ? (
        <div className="text-muted-foreground text-sm py-12 text-center">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            <Kpi icon={Handshake} label="Consent events" value={d.consents.total ?? 0} tone="teal" testid="kpi-consents" />
            <Kpi icon={UserCheck} label="Open privacy requests" value={d.dsar.open ?? 0} tone="sky" testid="kpi-dsar-open" />
            <Kpi icon={AlertTriangle} label="Overdue requests" value={d.dsar.overdue ?? 0} tone="amber" testid="kpi-dsar-overdue" />
            <Kpi icon={ShieldAlert} label="Consents withdrawn" value={d.consents.withdrawn ?? 0} tone="red" testid="kpi-withdrawn" />
            <Kpi icon={Boxes} label="PHI data categories" value={d.inventory.phi ?? 0} tone="rose" testid="kpi-phi" />
            <Kpi icon={ScrollText} label="Retention-flagged records" value={d.retention.flagged ?? "—"} tone="amber" testid="kpi-retention" />
          </div>

          <div className="bg-card rounded-xl border border-border p-5" data-testid="dsar-status-breakdown">
            <h3 className="text-sm font-semibold text-foreground mb-3">Privacy requests by status</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(d.dsar.byStatus || {}).map(([s, n]) => (
                <span key={s} className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm">
                  <Badge label={s} kind="status" /> <span className="font-semibold text-foreground">{n}</span>
                </span>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
